import { Entity, Location, OptimizerConfig, TrafficOptions } from '@nx-smartmonkey/shared/domain';
import { removeEmpty } from '@nx-smartmonkey/shared/helpers';
import configuration from '../../services/configuration';
import { ProjectCustomFields } from './ProjectCustomFields';
import { ProjectCustomerEmailTypes, ProjectCustomerSMSTypes } from './ProjectDTO';
import { ProjectPlans } from './ProjectPlans';
import { ProjectUnits } from './ProjectUnits';
import { ProjectConstraints } from './projectConstraints/ProjectConstraints';
import { ProjectCustomerEmail } from './projectCustomerEmail/ProjectCustomerEmail';
import { ProjectCustomerSMS } from './projectCustomerSMS/ProjectCustomerSMS';
import { ProjectCustomerWhatsApp } from './projectCustomerWhatsApp/ProjectCustomerWhatsApp';
import { ProjectCustomers } from './projectCustomers/ProjectCustomers';
import { ProjectDrivers } from './projectDrivers/ProjectDrivers';
import { ProjectPlanner } from './projectPlanner/ProjectPlanner';
import { ProjectSupervisors } from './projectSupervisors/ProjectSupervisors';

export interface UpdateProjectProps {
  label?: string | null;
  description?: string | null;
  location?: Location | null;
  optimizer_config?: OptimizerConfig;
  traffic_options?: TrafficOptions;
  units?: ProjectUnits;
  time_zone?: string;
  plans?: ProjectPlans;
  tasks_enabled?: boolean;
  geo_fences_enabled?: boolean;
  constraints?: ProjectConstraints;
  customers?: ProjectCustomers;
  drivers?: ProjectDrivers;
  planner?: ProjectPlanner;
  supervisors?: ProjectSupervisors;
  project_slug?: string;
}

export interface ProjectProps {
  organization_id: string;
  label?: string;
  public_key?: string;
  location?: Location;
  description?: string;
  avatar_url?: string;
  optimizer_config?: OptimizerConfig;
  traffic: boolean;
  traffic_options?: TrafficOptions;
  units?: ProjectUnits;
  time_zone?: string;
  plans: ProjectPlans;

  custom_fields?: ProjectCustomFields;
  customer_email: ProjectCustomerEmail;
  customer_sms: ProjectCustomerSMS;
  customer_whatsapp: ProjectCustomerWhatsApp;

  tasks_enabled: boolean;
  geo_fences_enabled: boolean;
  constraints: ProjectConstraints;

  customers: ProjectCustomers;
  drivers: ProjectDrivers;
  planner: ProjectPlanner;
  supervisors: ProjectSupervisors;
  project_slug: string;
  managed: boolean;
  max_managed_vehicles: number;

  deleted?: boolean;
  created_by: string;
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
}

export class Project extends Entity<ProjectProps> {
  private _avatar: string | undefined;

  get id(): string {
    return this._id!;
  }
  get organization_id(): string {
    return this.props.organization_id;
  }
  get label(): string | undefined {
    return this.props.label;
  }
  set label(label: string | undefined) {
    this.props.label = label;
  }
  get public_key(): string | undefined {
    return this.props.public_key;
  }
  get location(): Location | undefined {
    return this.props.location;
  }
  set location(location: Location | undefined) {
    this.props.location = location;
  }
  get description(): string | undefined {
    return this.props.description;
  }
  set description(description: string | undefined) {
    this.props.description = description;
  }
  get avatar_url(): string | undefined {
    return this.props.avatar_url;
  }
  set avatar_url(avatar_url: string | undefined) {
    this.props.avatar_url = avatar_url;
  }
  get project_avatar(): string | undefined {
    return this._avatar;
  }
  set project_avatar(project_avatar: string | undefined) {
    this._avatar = project_avatar;
  }
  get optimizer_config(): OptimizerConfig | undefined {
    return this.props.optimizer_config;
  }
  set optimizer_config(optimizer_config: OptimizerConfig | undefined) {
    this.props.optimizer_config = optimizer_config;
  }
  set traffic(traffic: boolean) {
    this.props.traffic = traffic;
  }
  get traffic(): boolean {
    return this.props.traffic;
  }
  get traffic_options(): TrafficOptions | undefined {
    return this.props.traffic_options;
  }
  set traffic_options(traffic_options: TrafficOptions | undefined) {
    this.props.traffic_options = traffic_options;
  }
  get units(): ProjectUnits | undefined {
    return this.props.units;
  }
  set units(units: ProjectUnits | undefined) {
    this.props.units = units;
  }
  get time_zone(): string | undefined {
    return this.props.time_zone;
  }
  set time_zone(time_zone: string | undefined) {
    this.props.time_zone = time_zone;
  }
  get plans(): ProjectPlans {
    return this.props.plans;
  }
  set plans(plans: ProjectPlans) {
    this.props.plans = plans;
  }
  get custom_fields(): ProjectCustomFields | undefined {
    return this.props.custom_fields;
  }
  get customer_email(): ProjectCustomerEmail {
    return this.props.customer_email;
  }
  get customer_sms(): ProjectCustomerSMS {
    return this.props.customer_sms;
  }
  get customer_whatsapp(): ProjectCustomerWhatsApp {
    return this.props.customer_whatsapp;
  }
  get tasks_enabled(): boolean {
    return this.props.tasks_enabled;
  }
  set tasks_enabled(tasks_enabled: boolean) {
    this.props.tasks_enabled = tasks_enabled;
  }
  get geo_fences_enabled(): boolean {
    return this.props.geo_fences_enabled;
  }
  set geo_fences_enabled(geo_fences_enabled: boolean) {
    this.props.geo_fences_enabled = geo_fences_enabled;
  }
  get constraints(): ProjectConstraints {
    return this.props.constraints;
  }
  set constraints(constraints: ProjectConstraints) {
    this.props.constraints = constraints;
  }
  get customers(): ProjectCustomers {
    return this.props.customers;
  }
  set customers(customers: ProjectCustomers) {
    this.props.customers = customers;
  }
  get drivers(): ProjectDrivers {
    return this.props.drivers;
  }
  set drivers(drivers: ProjectDrivers) {
    this.props.drivers = drivers;
  }
  get planner(): ProjectPlanner {
    return this.props.planner;
  }
  set planner(planner: ProjectPlanner) {
    this.props.planner = planner;
  }
  get supervisors(): ProjectSupervisors {
    return this.props.supervisors;
  }
  set supervisors(supervisors: ProjectSupervisors) {
    this.props.supervisors = supervisors;
  }
  get project_slug(): string {
    return this.props.project_slug;
  }
  set project_slug(project_slug: string) {
    this.props.project_slug = project_slug;
  }
  get managed(): boolean {
    return this.props.managed;
  }
  get max_managed_vehicles(): number {
    return this.props.max_managed_vehicles;
  }
  get deleted(): boolean | undefined {
    return this.props.deleted;
  }
  get created_by(): string | undefined {
    return this.props.created_by;
  }
  get created_at(): Date | undefined {
    return this.props.created_at;
  }
  get updated_at(): Date | undefined {
    return this.props.updated_at;
  }
  get deleted_at(): Date | undefined {
    return this.props.deleted_at;
  }
  protected constructor(props: ProjectProps, id?: string) {
    super(props, id);
  }

  static create(props: ProjectProps, id?: string) {
    return new Project(props, id);
  }

  update(props: UpdateProjectProps) {
    removeEmpty(props);
    if (props.label) this.label = props.label;
    if (props.description) this.description = props.description;
    if (props.location) this.location = props.location;
    if (props.optimizer_config) this.optimizer_config = props.optimizer_config;
    if (props.traffic_options) this.traffic_options = props.traffic_options;
    if (props.units) this.units = props.units;
    if (props.plans) this.plans = props.plans;
    if (props.tasks_enabled !== undefined) this.tasks_enabled = props.tasks_enabled;
    if (props.geo_fences_enabled !== undefined) this.geo_fences_enabled = props.geo_fences_enabled;
    if (props.customers !== undefined) this.customers = props.customers;
    if (props.drivers !== undefined) this.drivers = props.drivers;
    if (props.planner !== undefined) this.planner = props.planner;
    if (props.supervisors !== undefined) this.supervisors = props.supervisors;
    if (props.project_slug) this.project_slug = props.project_slug;
    if (props.time_zone) this.time_zone = props.time_zone;
  }

  getCustomersDomain() {
    const customDomain = this.customers.style.custom_domain ?? ``;
    return customDomain !== `` ? customDomain : this.getCustomersProjectSlugDomain();
  }

  getCustomersProjectSlugDomain() {
    return configuration.CUSTOMERS_SLUG_ENDPOINT.replace(`{project_slug}`, this.project_slug);
  }

  getSupervisorsDomain() {
    const customDomain = this.supervisors.style.custom_domain ?? ``;
    return customDomain !== `` ? customDomain : this.getSupervisorsProjectSlugDomain();
  }

  getSupervisorsProjectSlugDomain() {
    return configuration.SUPERVISORS_SLUG_ENDPOINT.replace(`{project_slug}`, this.project_slug);
  }

  isCustomersSurveyEnabled() {
    return this.customers.fields.find((field) => field.type === `survey`) !== undefined;
  }

  getCustomerAvailableEmails = () => {
    return this.customer_email.getCustomerAvailableEmails({ pickupsEnabled: this.constraints.pickups });
  };

  getCustomerAvailableEmailsTypes = () => {
    return this.customer_email.getCustomerAvailableEmailsTypes(this.constraints.pickups);
  };

  countCustomerAvailableEmails = () => {
    return this.customer_email.countCustomerAvailableEmails(this.constraints.pickups);
  };

  countCustomerEnabledEmails = () => {
    return this.customer_email.countCustomerEnabledEmails(this.constraints.pickups);
  };

  isEmailEnabled = (emailType: ProjectCustomerEmailTypes) => {
    return this.customer_email.isEmailEnabled(emailType, this.constraints.pickups);
  };

  getTranslationsEmailType = (emailType: ProjectCustomerEmailTypes) => {
    if (emailType === `stop_approaching`) {
      return `service_approaching`;
    }

    if (emailType === `stop_completed`) {
      return `service_completed`;
    }

    if (emailType === `stop_canceled`) {
      return `service_canceled`;
    }

    return emailType;
  };

  getCustomerAvailableSMS = () => {
    return this.customer_sms.getCustomerAvailableSMS({ pickupsEnabled: this.constraints.pickups });
  };

  getCustomerAvailableSMSTypes = () => {
    return this.customer_sms.getCustomerAvailableSMSTypes(this.constraints.pickups);
  };

  countCustomerAvailableSMS = () => {
    return this.customer_sms.countCustomerAvailableSMS(this.constraints.pickups);
  };

  countCustomerEnabledSMS = () => {
    return this.customer_sms.countCustomerEnabledSMS(this.constraints.pickups);
  };

  isSMSEnabled = (emailType: ProjectCustomerSMSTypes) => {
    return this.customer_sms.isSMSEnabled(emailType, this.constraints.pickups);
  };

  getTranslationsSMSType = (smsType: ProjectCustomerSMSTypes) => {
    if (smsType === `stop_approaching`) {
      return `service_approaching`;
    }

    if (smsType === `stop_completed`) {
      return `service_completed`;
    }

    if (smsType === `stop_canceled`) {
      return `service_canceled`;
    }

    return smsType;
  };
}
